import {
  getAccessToken,
  setAccessToken,
  clearUserToken,
  getSubId,
  isValidAccessToken,
} from '@devhacker/shared/features/auth/utils/token';
import type { EmailPolicy } from '@devhacker/shared/server/bff/settingsSection';
import { useSystemPush } from '@devhacker/shared/features/system-push/useSystemPush';
import { normalizeUserData } from '~/libs/normalizeUserData';
import {
  connectPrivateChannel,
  fetchUnviewedNotificationsCount,
} from '~/components/Pusher/websockets-functions';
import { useIndexStore } from '~/store';
import { isRequiresAuth } from '~/utils';
import { shouldOpenProfileSetup } from '@devhacker/shared/features/auth/utils/shouldOpenProfileSetup';
import { useTalkerStore } from '@devhacker/shared/plugins/talker/store/talker';
import { refreshTokenSingleFlight } from '@devhacker/shared/features/auth/utils/refreshSingleFlight';
import { bootFromHash } from '@devhacker/shared/features/auth/utils/authRedirect';
import { requestEmailChange, resendEmailConfirmation } from '@devhacker/shared/features/auth/api';
import { patchProfileUser } from '@devhacker/shared/features/profile/api';

export default defineNuxtPlugin(() => {
  const route = useRoute();
  const indexStore = useIndexStore();

  async function fetchUserData(): Promise<any> {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    const subId = getSubId(accessToken);
    if (!subId) return;

    const response = await indexStore.fetchUser(subId).catch((error: any) => {
      if (error?.response?.status === 403) {
        indexStore.clearUserAndRelatedData();

        if (isRequiresAuth(route)) {
          console.warn('redirect to /');
          location.href = '/';
          return;
        }
      }
    });

    const userInStore = indexStore.user;
    userInStore && useTalkerStore().setUser(normalizeUserData(userInStore));

    // Delayed login action
    indexStore.afterLoginAction && indexStore.afterLoginAction();

    return response;
  }

  /**
   * Политика email-доменов для попапа дозаполнения: домены, текст отказа и адрес
   * поддержки — всё из auth-sso через BFF.
   * Fail-open: при любой ошибке отдаём пустую политику — на фронте это означает
   * «домен не проверяем», блокировать сохранение профиля из-за настроек нельзя.
   * Таймаут обязателен: попап открывается ПОСЛЕ этого запроса, и без него
   * зависший ответ означает, что попап не покажется вообще.
   */
  async function fetchEmailPolicy(): Promise<EmailPolicy> {
    try {
      return await $fetch<EmailPolicy>('/bff/allowed-email-domains', {
        signal: AbortSignal.timeout(3000),
      });
    } catch (error) {
      console.error('failed to fetch allowed email domains', error);
      return { domains: [], message: '', support_email: '' };
    }
  }

  async function openProfileSetupIfNeeded() {
    const user = indexStore.user;
    if (!user) return;
    if (!shouldOpenProfileSetup(user)) return;

    const ownerId = getSubId(getAccessToken());
    if (!ownerId) return;

    const emailPolicy = await fetchEmailPolicy();

    // Третья сущность очереди системных пушей: после cookie-баннера и пуша, cooldown 12 ч.
    useSystemPush().requestProfileSetup({
      id: ownerId,
      name: user.display_name || '',
      email: user.email || '',
      emailTemp: user.email_temp || '',
      emailPolicy,
      onResendEmail: () => resendEmailConfirmation(ownerId, { redirect_url: location.href }),
      onSubmit: async (payload: { display_name?: string; email?: string }) => {
        if (payload.email) {
          await requestEmailChange(ownerId, {
            email: payload.email,
            redirect_url: location.href,
          });
        }

        try {
          await patchProfileUser(
            ownerId,
            payload.display_name ? { display_name: payload.display_name } : {},
          );
        } catch (error) {
          if (payload.email) {
            console.error('profile patch failed after email change was accepted', error);
          }
          throw error;
        }

        indexStore.patchUser({
          ...(payload.display_name ? { display_name: payload.display_name } : {}),
          ...(payload.email ? { email_temp: payload.email } : {}),
        });
      },
    });
  }

  async function Initialize(): Promise<void> {
    // 1. Попробовать bootFromHash — если вернулся с auth-sso
    bootFromHash();
    void indexStore.fetchIsWpLoggedIn();

    const accessToken = getAccessToken();

    if (accessToken && getSubId(accessToken)) {
      if (isValidAccessToken(accessToken)) {
        // Токен есть и валиден → загрузить данные пользователя
        fetchUserData()
          .then(() => {
            void openProfileSetupIfNeeded();
            connectPrivateChannel();
            fetchUnviewedNotificationsCount();
          })
          .catch((err) => {
            console.error('fetchUserData in Initialize', err);
          });
      } else {
        // Токен есть но протух → попытка silent refresh
        try {
          const result = await refreshTokenSingleFlight();
          if (result.access_token) {
            setAccessToken(result.access_token);
            fetchUserData()
              .then(() => {
                void openProfileSetupIfNeeded();
                connectPrivateChannel();
                fetchUnviewedNotificationsCount();
              })
              .catch((err) => {
                console.error('fetchUserData after refresh', err);
              });
          } else {
            // Refresh прошёл без ошибки, но access_token не получен — очищаем сессию
            console.warn('Silent refresh returned empty access_token, clearing session');
            clearUserToken();
            indexStore.clearUserAndRelatedData();
            if (isRequiresAuth(route)) {
              location.href = '/';
              return;
            }
          }
        } catch (err) {
          console.warn('Silent refresh failed, user remains guest');
          // Если на protected route → redirect на главную
          if (isRequiresAuth(route)) {
            console.warn('redirect to /');
            location.href = '/';
            return;
          }
        }
      }
    } else {
      // Нет токена — гость
      if (isRequiresAuth(route)) {
        console.warn('redirect to /');
        location.href = '/';
        return;
      }
    }
  }

  onNuxtReady(() => {
    void Initialize().catch((err) => {
      console.error('Initialize', err);
    });
  });
});
